import React, { useState, useEffect } from 'react';
import { Bot, Plus } from 'lucide-react';
import { chatbotsAPI } from '../services/api';
import Modal from '../components/Modal';

const Chatbot = () => {
  const [chatbots, setChatbots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newBot, setNewBot] = useState({ triggers: [] });
  const [triggerInput, setTriggerInput] = useState('');

  useEffect(() => {
    loadChatbots();
  }, []);

  const loadChatbots = async () => {
    try {
      setLoading(true);
      const response = await chatbotsAPI.getAll();
      setChatbots(response.data || []);
    } catch (error) {
      console.error('Error loading chatbots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTrigger = () => {
    if (triggerInput.trim()) {
      setNewBot(prev => ({
        ...prev,
        triggers: [...(prev.triggers || []), triggerInput.trim()]
      }));
      setTriggerInput('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await chatbotsAPI.create({
        ...newBot,
        active: true
      });
      loadChatbots();
      setIsModalOpen(false);
      setNewBot({ triggers: [] });
    } catch (error) {
      console.error('Error creating chatbot:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="chatbot-page">
       <div className="flex justify-between items-center bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-800">Chatbot Builder</h2>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
          data-testid="add-chatbot-btn"
        >
          <Plus size={20} className="mr-2" /> Add Chatbot
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {chatbots.map(bot => (
            <div key={bot.id} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-indigo-100 p-2 rounded-lg text-indigo-600">
                        <Bot size={24} />
                    </div>
                    <h3 className="font-bold text-gray-900">{bot.name}</h3>
                </div>
                <div className="space-y-2 mb-4">
                    <p className="text-xs text-gray-500 uppercase font-semibold">Triggers:</p>
                    <div className="flex flex-wrap gap-2">
                        {(bot.triggers || []).map((t, idx) => (
                            <span key={idx} className="bg-gray-100 px-2 py-1 rounded text-xs">{t}</span>
                        ))}
                        {(!bot.triggers || bot.triggers.length === 0) && (
                          <span className="text-gray-400 text-xs">No triggers set</span>
                        )}
                    </div>
                </div>
                <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                    <span className={`text-sm font-medium ${bot.active ? 'text-green-600' : 'text-gray-500'}`}>
                      {bot.active ? 'Active' : 'Inactive'}
                    </span>
                    <button className="text-sm text-teal-600 font-medium hover:underline">Edit Flow</button>
                </div>
            </div>
        ))}
        {chatbots.length === 0 && (
          <div className="col-span-full bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center text-gray-500">
            No chatbots configured yet
          </div>
        )}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Add Chatbot">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Chatbot Name</label>
            <input 
              required 
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" 
              value={newBot.name || ''}
              onChange={e => setNewBot({...newBot, name: e.target.value})} 
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Triggers</label>
            <div className="flex gap-2">
              <input 
                className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none" 
                placeholder="Enter trigger keyword..."
                value={triggerInput}
                onChange={e => setTriggerInput(e.target.value)} 
              />
              <button 
                type="button"
                onClick={handleAddTrigger}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {(newBot.triggers || []).map((t, idx) => (
                <span key={idx} className="bg-teal-100 text-teal-700 px-2 py-1 rounded text-xs flex items-center">
                  {t}
                  <button 
                    type="button"
                    onClick={() => setNewBot(prev => ({
                      ...prev,
                      triggers: prev.triggers.filter((_, i) => i !== idx)
                    }))}
                    className="ml-1 hover:text-red-500"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
          <button type="submit" className="w-full bg-teal-600 text-white py-2 rounded-lg hover:bg-teal-700">Create Chatbot</button>
        </form>
      </Modal>
    </div>
  );
};

export default Chatbot;
